import React, { useMemo } from 'react';

import { ChevronDown } from 'lucide-react';
import { SKILL_SCOPE_ICONS } from 'src/components/ai/chat/ChatPanel/ChatEditor/ChatEditorSkill/skill-appearance';
import { DeclarativeMenu } from 'src/components/declarative-menu';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { SKILL_SCOPE_LABELS } from './skill-scope-labels';

export type EditableSkillScope = Apollo.AgentSkillScope.Personal | Apollo.AgentSkillScope.Org;

const EDITABLE_SCOPES = [Apollo.AgentSkillScope.Personal, Apollo.AgentSkillScope.Org] as const;

const useStyles = makeStyles()(theme => ({
  label: { alignItems: 'center', display: 'inline-flex', fontSize: 14, gap: 6 },
  trigger: {
    '&:disabled': { cursor: 'default', opacity: 0.6 },
    '&:hover:not(:disabled)': { backgroundColor: theme.palette.action.hover },
    alignItems: 'center',
    background: 'transparent',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    color: theme.palette.text.primary,
    cursor: 'pointer',
    display: 'inline-flex',
    font: 'inherit',
    fontSize: 14,
    gap: 4,
    height: 24,
    lineHeight: '16px',
    padding: '2px 6px',
    whiteSpace: 'nowrap',
  },
}));

/** The same access control in the library and the editor. Selecting Org shares the skill. */
export const SkillAccessMenu = React.memo(function SkillAccessMenu({
  busy = false,
  canEdit,
  onChange,
  scope,
}: {
  canEdit: boolean;
  onChange: (scope: EditableSkillScope) => void;
  scope: Apollo.AgentSkillScope;
  busy?: boolean;
}) {
  const { classes } = useStyles();
  const Icon = SKILL_SCOPE_ICONS[scope];
  const menu = useMemo(
    () =>
      EDITABLE_SCOPES.map(target => {
        const ScopeIcon = SKILL_SCOPE_ICONS[target];
        return {
          checked: scope === target,
          enabled: !busy,
          icon: <ScopeIcon size={16} />,
          label: SKILL_SCOPE_LABELS[target],
          onClick: () => {
            if (target !== scope) onChange(target);
          },
        };
      }),
    [busy, onChange, scope]
  );
  const label = (
    <span className={classes.label}>
      <Icon size={16} />
      {SKILL_SCOPE_LABELS[scope]}
    </span>
  );
  if (!canEdit || scope === Apollo.AgentSkillScope.Community) return label;
  return (
    <DeclarativeMenu config={menu} disabled={busy}>
      {(props, state) => (
        <button
          {...props}
          aria-busy={busy}
          aria-label="Change skill access"
          className={classes.trigger}
          disabled={busy || state.disabled}
          type="button"
        >
          {label}
          <ChevronDown size={14} />
        </button>
      )}
    </DeclarativeMenu>
  );
});
